import { UIElement } from '../ui-element';
import { element } from '../../global/template-literals';
import {
    getSiblingsAfter,
    getSiblingsBefore,
    Mapped,
} from '../../global/ui-helpers';
import { Digest } from '../../global/index';
import { TabIndex } from '../../global/keyboard';
import { Labels } from '../../global/labels';
import { debounce } from '../../global/helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { TranslationLabels } from '../../types/labels.type';
import type { EventHandler } from '../../global/utils.type';
import type { UIAsset } from '../index';
import type {
    UIAssetsContentToggleLayout,
    UIAssetsContentToggleLayouts,
} from './ui-assets.type';
import styles from './ui-assets.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAssets
 * @element ui-assets
 * @classdesc Represents a class for <code>ui-assets</code> element.
 * Container for ui-asset. Can have only UIAsset children.
 * @property {string} [labelMore="Show more"] {@attr label-more} Content toggle button label
 * when content is collapsed.
 * @property {string} [labelLess="Show less"] {@attr label-less} Content toggle button label
 * when content is expanded.
 * @property {number} [visibleAssets=5] {@attr visible-assets} How many assets
 * will stay visible when the list is collapsed.
 * @property {boolean} [notoggle=false] {@attr notoggle} Will disable the content toggle.
 * @property {boolean} [expanded=false] {@attr expanded} If list will
 * be initially rendered expanded.
 * @property {("default" | "tertiary-button")} [contentToggleLayout="default"]
 * {@attr content-toggle-layout} Visual layout of the content toggle button.
 *  {@desc default: icon followed by label}
 *  {@desc tertiary-button: label followed by icon, styled as a small tertiary button}
 * @property {HTMLDivElement} contentToggleElement Content toggle container element.
 * @property {HTMLDivElement} contentToggleCtaElement Content toggle button element.
 * @property {HTMLDivElement} contentToggleCtaIconElement Content toggle button
 * icon element.
 * @property {HTMLDivElement} contentToggleCtaLabelElement Content toggle button
 * label element.
 * @property {HTMLDivElement} contentToggleDescriptionElement Content toggle button
 * description element.
 * @property {NodeList<UIAsset>} assets All the child asset elements.
 * @slot {@type "ui-asset"}
 * @example
 * <ui-assets>
 *   <ui-asset
 *     label="Main account"
 *     label-secondary="EE345678909870901345"
 *   >
 *     <ui-badge slot="badge" layout="pin" color="pineapple">Default</ui-badge>
 *     <span slot="value"><ui-currency value="1200.48"></ui-currency> EUR</span>
 *   </ui-asset>
 *   <ui-asset
 *     label="Randy Lahey"
 *     label-secondary="EE323601345251978909"
 *     value-secondary="+2 currencies"
 *   >
 *     <span slot="value"><ui-currency value="12870.72"></ui-currency> EUR</span>
 *   </ui-asset>
 *   <ui-asset
 *     label="Credit card account"
 *     label-secondary="EE319781678251782328"
 *   >
 *     <span slot="value"><ui-currency value="900.35"></ui-currency> EUR</span>
 *   </ui-asset>
 *   <ui-asset
 *     label="Easy saver account"
 *     label-secondary="EE320783348252214302"
 *   >
 *     <span slot="value"><ui-currency value="500.40"></ui-currency> EUR</span>
 *   </ui-asset>
 *   <ui-asset
 *     label="Investment account"
 *     label-secondary="LHV | EE344981345133277795"
 *   >
 *     <span slot="value"><ui-currency value="200.42"></ui-currency> EUR</span>
 *   </ui-asset>
 *   <ui-asset
 *     label="Child’s account"
 *     label-secondary="EE315675538251764701"
 *   >
 *     <span slot="value"><ui-currency value="1000"></ui-currency> EUR</span>
 *   </ui-asset>
 * </ui-assets>
 */
@Component(
    {
        tag: 'ui-assets',
        styles,
    },
    Mapped.True
)
export class UIAssets extends UIElement {
    /**
     * Content toggle button layout options.
     * @type {UIAssetsContentToggleLayouts}
     * @readonly
     */
    static readonly contentToggleLayouts: UIAssetsContentToggleLayouts = {
        Default: 'default',
        TertiaryButton: 'tertiary-button',
    };

    @Attribute(String, Mapped.True) labelMore?: TranslationLabels.Label;
    @Attribute(String, Mapped.True) labelLess?: TranslationLabels.Label;
    @Attribute(Number, Mapped.True) visibleAssets = 5;
    @Attribute(Boolean, Mapped.True) notoggle?: boolean;
    @Attribute(Boolean, Mapped.True) expanded?: boolean;
    @Attribute(String, Mapped.True)
    contentToggleLayout: UIAssetsContentToggleLayout =
        UIAssets.contentToggleLayouts.Default;

    @Child('.ui-assets__content-toggle') contentToggleElement!: HTMLDivElement;
    @Child('.ui-assets__content-toggle-cta')
    contentToggleCtaElement!: HTMLDivElement;
    @Child('.ui-assets__content-toggle-cta-icon')
    contentToggleCtaIconElement!: HTMLDivElement;
    @Child('.ui-assets__content-toggle-cta-label')
    contentToggleCtaLabelElement!: HTMLDivElement;
    @Child('.ui-assets__content-toggle-description')
    contentToggleDescriptionElement!: HTMLDivElement;
    @Children('ui-asset', Mapped.True) assets!: NodeListOf<UIAsset>;

    private contentElements: Array<Element> = [];
    private _id!: string;
    private _descriptionId!: string;
    private _hasContentToggle?: boolean;
    private minimizedHeight = 0;
    private fullHeight = 0;
    private contentToggleHandler: EventHandler | null = null;
    private resizeHandler: EventHandler | null = null;

    /**
     * @type {string}
     */
    get id(): string {
        return this._id || (this._id = Digest.randomId()); // remove assertions when digest is migrated to ts
    }

    /**
     * @type {string}
     */
    get descriptionId(): string {
        return this._descriptionId || (this._descriptionId = Digest.randomId()); // remove assertions when digest is migrated to ts
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-assets', {
            labelMore: 'Show more',
            labelLess: 'Show less',
            labelToggleContent:
                'Currently {visibleAssets} of {allAssets} list items visible.',
        });
    }

    /**
     * @type {string}
     * @private
     */
    private get toggleLabel(): TranslationLabels.Label {
        return this.expanded
            ? this.labelLess || this.getLabel('labelLess')
            : this.labelMore || this.getLabel('labelMore');
    }

    /**
     * @type {string}
     * @private
     */
    private get currentlyVisibleAssets(): string {
        return `${this.expanded ? this.assets.length : this.visibleAssets}`;
    }

    /**
     * All visible elements including assets, titles, dividers
     * @type {HTMLElement[]}
     * @private
     */
    private get visibleElements(): Array<Element> {
        // If there are no assets there will be no toggle button so nothing is hidden (dividers, titles)
        if (!this.assets) {
            return getSiblingsBefore(this.contentToggleElement) || [];
        }
        // If visible assets is set to 0, everything is hidden behind a toggle button so nothing visible
        if (!this.visibleAssets) {
            return [];
        }

        const lastVisibleAsset = this.assets[this.visibleAssets];
        return getSiblingsBefore(lastVisibleAsset);
    }

    /**
     * All hidden elements including assets, titles, dividers
     * @type {Element[]}
     * @private
     */
    private get hiddenElements(): Array<Element> {
        // If there are no assets there will be no toggle button so nothing is hidden
        if (!this.assets) {
            return [];
        }

        // If visible assets is set to 0, all are hidden so get every (hidden) element
        if (!this.visibleAssets) {
            return getSiblingsBefore(this.contentToggleElement);
        }

        // If there are visible assets, get everything after the last visible one
        const lastVisibleAsset = this.assets[this.visibleAssets];
        return getSiblingsAfter(lastVisibleAsset);
    }

    /**
     * List of content elements formatted so that the last item is always ui-asset:
     *   - Includes ui-assets
     *   - Includes titles and dividers
     *   - Everything after last ui-asset is removed (no trailing titles and dividers)
     *   - Content toggle and accessibility state element are excluded
     * Should be called in case content toggle is present.
     *
     * Private - only exposed for testing
     * @type {Element[]}
     */
    get allContentElements(): Array<Element> {
        const allElements = [...this.children]; // Splice doesn't work otherwise
        const ignoredElementsCount = 2;

        // Remove last content element from the array in case it is not an asset (divider, title)
        // Last two elements are content toggle and accessibility state, so we ignore these
        while (
            allElements.length > ignoredElementsCount &&
            allElements[allElements.length - (ignoredElementsCount + 1)]
                .localName !== 'ui-asset'
        ) {
            allElements.splice(
                allElements.length - (ignoredElementsCount + 1),
                1
            );
        }

        return allElements;
    }

    /**
     * @type {boolean}
     * @private
     */
    private get hasContentToggle(): boolean {
        if (this._hasContentToggle === undefined) {
            // Disabled manually by configuration
            const isTogglingDisabled = this.notoggle;
            // Disabled automatically if not enough assets
            const hasAssetsToHide = this.assets.length > this.visibleAssets;

            this._hasContentToggle = !isTogglingDisabled && hasAssetsToHide;
        }

        return !!this._hasContentToggle;
    }

    /**
     * @type {HTMLElement}
     * @private
     */
    private getContentToggleCtaElement(): HTMLElement {
        if (
            this.contentToggleLayout ===
            UIAssets.contentToggleLayouts.TertiaryButton
        ) {
            /* TODO: replace with correct tertiary button once https://jira.swedbank.net/browse/GUI-4411 is developed */
            return element`
          <div class="ui-assets__content-toggle">
            <button
              class="ui-assets__content-toggle-cta"
              aria-controls="${this.id}"
              aria-expanded="${this.expanded}"
              aria-describedby="${this.descriptionId}"
            >
              <span class="ui-assets__content-toggle-cta-label">
                ${this.toggleLabel}
              </span>
              <ui-icon class="ui-assets__content-toggle-cta-icon" glyph="redesign-chevron-down"></ui-icon>
            </button>
          </div>`;
        }

        return element`
          <div class="ui-assets__content-toggle">
            <button
              class="ui-assets__content-toggle-cta"
              aria-controls="${this.id}"
              aria-expanded="${this.expanded}" 
              aria-describedby="${this.descriptionId}"
            >
              <ui-icon class="ui-assets__content-toggle-cta-icon" glyph="redesign-chevron-down"></ui-icon>
              <span class="ui-assets__content-toggle-cta-label">
                ${this.toggleLabel}
              </span>
            </button>
          </div>`;
    }

    /**
     * @type {HTMLElement}
     * @private
     */
    private getContentToggleDescElement(): HTMLElement {
        return element`
          <div
            id="${this.descriptionId}"
            class="ui-assets__content-toggle-description"
            hidden
          >
            ${this.getLabel('labelToggleContent', {
                visibleAssets: this.currentlyVisibleAssets,
                allAssets: `${this.assets.length}`,
            })}
          </div>`;
    }

    /**
     * Sets animation and accessibility attributes necessary for expanding and collapsing content.
     * @private
     */
    private adjustContentToggleStyles(): void {
        // Set attributes
        this.style.setProperty(
            '--ui-assets-height',
            `${this.expanded ? this.fullHeight : this.minimizedHeight}px`
        );
        this.style.setProperty(
            '--ui-assets-cta-icon-angle',
            `${this.expanded ? 180 : 0}deg`
        );
        this.contentToggleCtaElement.setAttribute(
            'aria-expanded',
            `${this.expanded}`
        );

        // Set labels
        this.contentToggleCtaLabelElement.innerText = this
            .toggleLabel as string;
        this.contentToggleDescriptionElement.innerText = this.getLabel(
            'labelToggleContent',
            {
                visibleAssets: this.currentlyVisibleAssets,
                allAssets: `${this.assets.length}`,
            }
        ) as string;

        // Handle hidden elements' accessibility
        const tabIndex = `${
            this.expanded ? TabIndex.Active : TabIndex.Inactive
        }`;
        const hiddenElements = this.hiddenElements;
        const hiddenElementsLength = hiddenElements.length;

        for (let index = 0; index < hiddenElementsLength; index++) {
            const assetContent = (this.hiddenElements[index] as UIAsset)
                ?.contentElement as Element;
            if (assetContent) {
                assetContent.setAttribute('aria-hidden', `${!this.expanded}`);
                assetContent.setAttribute('tabindex', tabIndex);
            }
        }
    }

    /**
     * Handles content expand/collapse.
     * @private
     */
    private handleContentToggle(): void {
        this.expanded = !this.expanded;
        this.adjustContentToggleStyles();
    }

    /**
     * Handles browser resize.
     * @private
     */
    private handleResize(): void {
        this.minimizedHeight = this.calculateMinimizedHeight();
        this.fullHeight = this.calculateFullHeight();

        this.classList.add('ui-assets__transitions-disabled');
        this.adjustContentToggleStyles();
        window.requestAnimationFrame(() => {
            this.classList.remove('ui-assets__transitions-disabled');
        });
    }

    /**
     * Calculate minimized height of asset list container when in closed state.
     * Should be called in case content toggle is present.
     * @type {number}
     * @private
     */
    private calculateMinimizedHeight(): number {
        // offsetHeight (not getBoundingClientRect) is used so measurements are the
        // element's layout height, not affected by ancestor transforms (e.g. the
        // scale() animation when a list opens inside a menu modal)
        let minimizedHeight = this.contentToggleElement.offsetHeight;

        let nonAssetElementHeight = 0;
        let assetCount = 0;

        // Calculate minimized list height
        for (const currentElement of this.contentElements) {
            // Enough assets visible already
            if (assetCount === this.visibleAssets) {
                break;
            }
            const isCurrentElementAssetType =
                currentElement.localName === 'ui-asset';

            // ASSET
            if (isCurrentElementAssetType) {
                // If previous item was not an asset, include its height in overall minimized height
                minimizedHeight += nonAssetElementHeight;
                // Include current asset height
                minimizedHeight += (currentElement as HTMLElement).offsetHeight;
                // Reset height for non-asset (such as divider or title)
                nonAssetElementHeight = 0;
                // To know how many assets to include
                assetCount += 1;
                continue;
            }
            // NON-ASSET
            nonAssetElementHeight += (currentElement as HTMLElement)
                .offsetHeight;
        }

        return minimizedHeight;
    }

    /**
     * Calculate full height of asset list container when in opened state.
     * Should be called in case content toggle is present.
     * @type {number}
     * @private
     */
    private calculateFullHeight(): number {
        // Calculate the full height of list
        const initialHeight = 0;
        return this.contentElements.reduce(
            (fullHeight, currentElement) =>
                fullHeight + (currentElement as HTMLElement).offsetHeight,
            initialHeight
        );
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    private addEventListeners(): void {
        // Initialize handlers if not already set
        if (!this.resizeHandler) {
            this.contentElements = this.allContentElements;
            const resizeHandler: () => void = this.handleResize.bind(this);
            this.resizeHandler = debounce(resizeHandler, 200);
            window.requestAnimationFrame(resizeHandler);
            window.addEventListener('resize', this.resizeHandler);
        }
        if (!this.contentToggleHandler) {
            this.contentToggleHandler = this.handleContentToggle.bind(this);
            this.contentToggleCtaElement.addEventListener(
                'click',
                this.contentToggleHandler
            );
        }
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    private removeEventListeners(): void {
        if (this.contentToggleHandler) {
            this.contentToggleCtaElement.removeEventListener(
                'click',
                this.contentToggleHandler
            );
            this.contentToggleHandler = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        // Set ARIA roles
        this.setAttribute('role', 'list');
        this.assets.forEach((asset) => {
            asset.setAttribute('role', 'listitem');
        });

        // Add content toggle if necessary
        if (this.hasContentToggle) {
            this.setAttribute('id', this.id);
            this.append(
                this.getContentToggleCtaElement(),
                this.getContentToggleDescElement()
            );
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.hasContentToggle && this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.hasContentToggle && this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.hasContentToggle && this.addEventListeners();
    }
}
